import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingTooltip } from './OnboardingTooltip';
import { CelebrationModal } from './CelebrationModal';
import { useOnboarding } from '../../hooks/useOnboarding';

// Guide steps for first_agent onboarding (team + agent creation flow)
type AgentCreationStep = 
  | 'add_team'           // Click + to add team
  | 'team_name'          // Enter team name
  | 'create_team'        // Click create team button
  | 'new_agent'          // Click new agent button
  | 'agent_name'         // Enter agent name
  | 'agent_subtitle'     // Enter subtitle
  | 'agent_instructions' // Enter instructions
  | 'agent_team'         // Select team
  | 'agent_create';      // Click create button

// Guide steps for first_test onboarding (test agent flow)
type TestStep = 
  | 'click_edit'
  | 'open_knowledge_tab'  // Click on Knowledge Base tab
  | 'assign_knowledge'
  | 'save_knowledge'
  | 'test_agent'
  | 'waiting_response'    // Waiting for agent response
  | 'complete';

type GuideStep = AgentCreationStep | TestStep;

interface AgentOnboardingGuideProps {
  isEditModalOpen: boolean;
  isCreateModalOpen?: boolean;
  isTeamModalOpen?: boolean;
}

export function AgentOnboardingGuide({
  isEditModalOpen,
  isCreateModalOpen = false,
  isTeamModalOpen = false,
}: AgentOnboardingGuideProps) {
  const navigate = useNavigate();
  const { progress, completeStep, fetchProgress } = useOnboarding();
  const [currentStep, setCurrentStep] = useState<GuideStep | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [guideEnabled, setGuideEnabled] = useState(false);
  const [testCompleted, setTestCompleted] = useState(false);
  const [celebrationDismissed, setCelebrationDismissed] = useState(false);
  const [knowledgeAssigned, setKnowledgeAssigned] = useState(false);
  const [knowledgeSaved, setKnowledgeSaved] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [knowledgeTabOpen, setKnowledgeTabOpen] = useState(false);
  
  // Track first_agent flow progress (independent of actual data)
  const [teamCreated, setTeamCreated] = useState(false);
  const [agentCreated, setAgentCreated] = useState(false);
  const [agentFormStep, setAgentFormStep] = useState<number>(0);
  
  // Skip initial minimize to allow widget to stay open after section completion
  const isInitialMount = useRef(true);
  // Skip minimize right after completing a step (widget should stay expanded)
  const justCompletedStep = useRef(false);

  // Refresh progress when component mounts
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Guide activation gate: only show tooltips when user explicitly started the guide
  useEffect(() => {
    const initial = localStorage.getItem('onboarding:guide-active') === 'true';
    setGuideEnabled(initial);

    const handleActivated = () => setGuideEnabled(true);
    const handleDeactivated = () => setGuideEnabled(false);
    window.addEventListener('onboarding:guide-activated', handleActivated);
    window.addEventListener('onboarding:guide-deactivated', handleDeactivated);
    return () => {
      window.removeEventListener('onboarding:guide-activated', handleActivated);
      window.removeEventListener('onboarding:guide-deactivated', handleDeactivated);
    };
  }, []);

  // Track when modals were open (to detect user actions vs existing data)
  const [teamModalWasOpen, setTeamModalWasOpen] = useState(false);
  const [agentModalWasOpen, setAgentModalWasOpen] = useState(false);

  // Listen for reset events
  useEffect(() => {
    const handleReset = () => {
      setDismissed(false);
      setTestCompleted(false);
      setKnowledgeAssigned(false);
      setKnowledgeSaved(false);
      setMessageSent(false);
      setKnowledgeTabOpen(false);
      setTeamCreated(false);
      setAgentCreated(false);
      setAgentFormStep(0);
      setTeamModalWasOpen(false);
      setAgentModalWasOpen(false);
    };
    
    window.addEventListener('onboarding:reset', handleReset);
    return () => {
      window.removeEventListener('onboarding:reset', handleReset);
    };
  }, []);

  // Check if we should show the agent onboarding guide
  const isOnboardingActive = progress?.status === 'in_progress' || progress?.status === 'not_started';
  
  // first_agent step: need to create an agent (don't require profile completion - user can click step directly)
  const isAgentStepPending = !progress?.steps_completed?.includes('first_agent');
  const isOnboardingAgentStep = isOnboardingActive && isAgentStepPending;
  
  // first_test step: need to test an agent (after knowledge is added)
  const isTestStepPending = !progress?.steps_completed?.includes('first_test');
  const isKnowledgeCompleted = progress?.steps_completed?.includes('first_knowledge');
  const isOnboardingTestStep = isOnboardingActive && isTestStepPending && isKnowledgeCompleted;
  
  // Show guide for either step, or if test just completed and celebration not yet dismissed
  const shouldShowGuide = guideEnabled && (isOnboardingAgentStep || isOnboardingTestStep || (testCompleted && !celebrationDismissed));

  // Track when team modal opens
  useEffect(() => {
    if (isTeamModalOpen) {
      setTeamModalWasOpen(true);
    }
  }, [isTeamModalOpen]);

  // Track when agent modal opens
  useEffect(() => {
    if (isCreateModalOpen) {
      setAgentModalWasOpen(true);
    }
  }, [isCreateModalOpen]);

  // Track when team modal closes AFTER being open (user created a team)
  useEffect(() => {
    if (!isOnboardingAgentStep) return;
    
    // Only mark as created if modal was open and is now closed
    if (teamModalWasOpen && !isTeamModalOpen && !teamCreated) {
      setTeamCreated(true);
    }
  }, [isTeamModalOpen, teamModalWasOpen, teamCreated, isOnboardingAgentStep]);

  // Track when agent modal closes AFTER being open (user created an agent)
  useEffect(() => {
    if (!isOnboardingAgentStep) return;
    
    // Complete when modal was open and is now closed (agent created)
    // Do not require team creation in this session: user can attach the agent to an existing team.
    if (agentModalWasOpen && !isCreateModalOpen && !agentCreated) {
      setAgentCreated(true);
      justCompletedStep.current = true;
      // Complete the first_agent step
      completeStep('first_agent');
      window.dispatchEvent(new CustomEvent('onboarding:step-completed'));
      window.dispatchEvent(new CustomEvent('onboarding:widget-expand'));
    }
  }, [isCreateModalOpen, agentModalWasOpen, agentCreated, isOnboardingAgentStep, completeStep]);

  // Track agent form progress
  useEffect(() => {
    if (!isOnboardingAgentStep || !isCreateModalOpen) {
      setAgentFormStep(0);
      return;
    }

    const checkFormProgress = () => {
      const nameInput = document.querySelector('[data-onboarding="agent-name-input"]') as HTMLInputElement;
      const subtitleInput = document.querySelector('[data-onboarding="agent-subtitle-input"]') as HTMLInputElement;
      const instructionsInput = document.querySelector('[data-onboarding="agent-instructions-input"]') as HTMLTextAreaElement;
      const teamSelect = document.querySelector('[data-onboarding="agent-team-select"]') as HTMLSelectElement;

      if (teamSelect && teamSelect.value) {
        setAgentFormStep(5); // Ready to create
      } else if (instructionsInput && instructionsInput.value.trim()) {
        setAgentFormStep(4); // Instructions filled
      } else if (subtitleInput && subtitleInput.value.trim()) {
        setAgentFormStep(3); // Subtitle filled
      } else if (nameInput && nameInput.value.trim()) {
        setAgentFormStep(2); // Name filled
      } else {
        setAgentFormStep(1); // Modal open, need name
      }
    };

    // Initial check with delay to let modal render
    const timeout = setTimeout(checkFormProgress, 200);

    const observer = new MutationObserver(checkFormProgress);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });

    // Also listen for input events
    const handleInput = () => setTimeout(checkFormProgress, 50);
    document.addEventListener('input', handleInput);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
      document.removeEventListener('input', handleInput);
    };
  }, [isOnboardingAgentStep, isCreateModalOpen]);

  // Check if knowledge tab is open (for first_test step)
  useEffect(() => {
    if (!isOnboardingTestStep || !isEditModalOpen || knowledgeTabOpen) return;

    const checkKnowledgeTabOpen = () => {
      const knowledgeSection = document.querySelector('[data-onboarding="knowledge-base-section"]');
      if (knowledgeSection) {
        setKnowledgeTabOpen(true);
      }
    };

    checkKnowledgeTabOpen();

    const observer = new MutationObserver(checkKnowledgeTabOpen);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isOnboardingTestStep, isEditModalOpen, knowledgeTabOpen]);

  // Check if knowledge is assigned (for first_test step)
  useEffect(() => {
    if (!isOnboardingTestStep || !isEditModalOpen || !knowledgeTabOpen) return;

    const checkKnowledgeAssignment = () => {
      const knowledgeSection = document.querySelector('[data-onboarding="knowledge-base-section"]');
      if (knowledgeSection) {
        const checkedBoxes = knowledgeSection.querySelectorAll('input[type="checkbox"]:checked');
        if (checkedBoxes.length > 0) {
          setKnowledgeAssigned(true);
        }
      }
    };

    checkKnowledgeAssignment();

    const observer = new MutationObserver(checkKnowledgeAssignment);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => observer.disconnect();
  }, [isOnboardingTestStep, isEditModalOpen, knowledgeTabOpen]);

  // Track when user clicks save button after assigning knowledge
  useEffect(() => {
    if (!isOnboardingTestStep || !isEditModalOpen || !knowledgeAssigned || knowledgeSaved) return;

    const handleSaveClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const saveBtn = target.closest('[data-onboarding="agent-save-btn"]');
      if (saveBtn) {
        setKnowledgeSaved(true);
      }
    };

    document.addEventListener('click', handleSaveClick);
    return () => document.removeEventListener('click', handleSaveClick);
  }, [isOnboardingTestStep, isEditModalOpen, knowledgeAssigned, knowledgeSaved]);

  // Listen for user sending a message in sandbox chat
  useEffect(() => {
    if (!isOnboardingTestStep || !isEditModalOpen || !knowledgeSaved || messageSent) return;

    const checkForUserMessage = () => {
      // Look for user messages in the chat (messages on the right side)
      const chatContainer = document.querySelector('[data-onboarding="sandbox-chat"]');
      if (chatContainer) {
        // Check for any message bubble that indicates user sent something
        const allMessages = chatContainer.querySelectorAll('[class*="justify-end"]');
        if (allMessages.length > 0) {
          setMessageSent(true);
        }
      }
    };

    const observer = new MutationObserver(checkForUserMessage);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isOnboardingTestStep, isEditModalOpen, knowledgeSaved, messageSent]);

  // Listen for agent response event to complete the step
  useEffect(() => {
    if (!isOnboardingTestStep || !isEditModalOpen || !messageSent || testCompleted) return;

    const handleAgentResponse = () => {
      setTestCompleted(true);
      setCelebrationDismissed(false);
      // Mark step as completed immediately (persist to DB)
      completeStep('first_test');
      window.dispatchEvent(new CustomEvent('onboarding:step-completed'));
    };

    window.addEventListener('onboarding:agent-response', handleAgentResponse);
    return () => window.removeEventListener('onboarding:agent-response', handleAgentResponse);
  }, [isOnboardingTestStep, isEditModalOpen, messageSent, testCompleted, completeStep]);

  // Determine current step based on state
  useEffect(() => {
    if (!shouldShowGuide || dismissed) {
      setCurrentStep(null);
      return;
    }

    // ===== first_agent step flow =====
    if (isOnboardingAgentStep) {
      // Team creation flow (independent of actual teams data)
      if (!teamCreated) {
        if (isTeamModalOpen) {
          // Check if team name is filled
          const teamInput = document.querySelector('[data-onboarding="team-name-input"]') as HTMLInputElement;
          if (teamInput && teamInput.value.trim()) {
            setCurrentStep('create_team');
          } else {
            setCurrentStep('team_name');
          }
        } else {
          setCurrentStep('add_team');
        }
        return;
      }

      // Agent creation flow (independent of actual agents data)
      if (!agentCreated) {
        if (isCreateModalOpen) {
          switch (agentFormStep) {
            case 1: setCurrentStep('agent_name'); break;
            case 2: setCurrentStep('agent_subtitle'); break;
            case 3: setCurrentStep('agent_instructions'); break;
            case 4: setCurrentStep('agent_team'); break;
            case 5: setCurrentStep('agent_create'); break;
            default: setCurrentStep('agent_name');
          }
        } else {
          setCurrentStep('new_agent');
        }
        return;
      }
    }

    // ===== first_test step flow =====
    // Show celebration if test just completed (even if step is already marked done in DB)
    if (testCompleted && !celebrationDismissed) {
      setCurrentStep('complete');
      return;
    }

    if (isOnboardingTestStep) {

      if (isEditModalOpen) {
        // Flow: open_knowledge_tab → assign_knowledge → save_knowledge → test_agent → waiting_response → complete
        if (!knowledgeTabOpen) {
          setCurrentStep('open_knowledge_tab');
        } else if (!knowledgeAssigned) {
          setCurrentStep('assign_knowledge');
        } else if (!knowledgeSaved) {
          setCurrentStep('save_knowledge');
        } else if (!messageSent) {
          setCurrentStep('test_agent');
        } else {
          setCurrentStep('waiting_response');
        }
        return;
      }

      // If first_knowledge is completed, user has agents - show click_edit
      setCurrentStep('click_edit');
      return;
    }

    setCurrentStep(null);
  }, [
    shouldShowGuide, isOnboardingAgentStep, isOnboardingTestStep, dismissed,
    isEditModalOpen, isCreateModalOpen, isTeamModalOpen,
    teamCreated, agentCreated, agentFormStep,
    testCompleted, celebrationDismissed, knowledgeTabOpen, knowledgeAssigned, knowledgeSaved, messageSent
  ]);

  // Notify widget when guide has active tooltip (to hide widget's own tooltip)
  useEffect(() => {
    if (currentStep && currentStep !== 'complete') {
      window.dispatchEvent(new CustomEvent('onboarding:guide-active'));
    } else {
      window.dispatchEvent(new CustomEvent('onboarding:guide-inactive'));
    }
  }, [currentStep]);

  // Auto-minimize widget when actively doing steps (skip on initial mount or after step completion)
  useEffect(() => {
    if (!currentStep) return;
    
    // Skip minimize on initial mount to let widget stay open after section completion
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Skip minimize right after completing a step
    if (justCompletedStep.current) {
      justCompletedStep.current = false;
      return;
    }
    
    // Steps that indicate active work - minimize widget
    const activeSteps: GuideStep[] = [
      'add_team', 'team_name', 'create_team',
      'new_agent', 'agent_name', 'agent_subtitle', 'agent_instructions', 'agent_team', 'agent_create',
      'click_edit', 'open_knowledge_tab', 'assign_knowledge', 'save_knowledge', 'test_agent', 'waiting_response'
    ];
    
    if (activeSteps.includes(currentStep)) {
      window.dispatchEvent(new CustomEvent('onboarding:widget-minimize'));
    }
  }, [currentStep]);

  const handleSkip = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleComplete = useCallback(() => {
    setCelebrationDismissed(true);
    setTestCompleted(false);
    // Close the sandbox modal first
    window.dispatchEvent(new CustomEvent('onboarding:close-sandbox'));
    setCurrentStep(null);
    window.dispatchEvent(new CustomEvent('onboarding:step-completed'));
    window.dispatchEvent(new CustomEvent('onboarding:widget-minimize'));
    localStorage.setItem('onboarding:guide-active', 'false');
    window.dispatchEvent(new CustomEvent('onboarding:guide-deactivated'));
    setTimeout(() => navigate('/'), 50);
  }, [navigate]);

  if (!shouldShowGuide || dismissed) {
    return null;
  }

  // Celebration modal for completion
  if (currentStep === 'complete') {
    return (
      <CelebrationModal
        isOpen={true}
        onClose={handleComplete}
      />
    );
  }

  if (!currentStep) {
    return null;
  }

  // Tooltip configurations
  const tooltipConfigs: Record<GuideStep, {
    targetSelector: string;
    title: string;
    description: string;
    position: 'top' | 'bottom' | 'left' | 'right';
    step: number;
    totalSteps: number;
  }> = {
    // Team creation flow (steps 1-3 of 9)
    add_team: {
      targetSelector: '[data-onboarding="add-team-btn"]',
      title: 'Créer une équipe',
      description: 'Cliquez sur le bouton + pour créer votre première équipe d\'agents.',
      position: 'bottom',
      step: 1,
      totalSteps: 9,
    },
    team_name: {
      targetSelector: '[data-onboarding="team-name-input"]',
      title: 'Nommer l\'équipe',
      description: 'Entrez un nom pour votre équipe, par exemple "Support" ou "Commercial".',
      position: 'left',
      step: 2,
      totalSteps: 9,
    },
    create_team: {
      targetSelector: '[data-onboarding="create-team-btn"]',
      title: 'Valider la création',
      description: 'Cliquez sur "Créer l\'équipe" pour finaliser.',
      position: 'top',
      step: 3,
      totalSteps: 9,
    },
    // Agent creation flow (steps 4-9 of 9)
    new_agent: {
      targetSelector: '[data-onboarding="new-agent-btn"]',
      title: 'Créer un agent',
      description: 'Cliquez sur "Créer un nouvel agent" pour configurer votre premier assistant IA.',
      position: 'bottom',
      step: 4,
      totalSteps: 9,
    },
    agent_name: {
      targetSelector: '[data-onboarding="agent-name-input"]',
      title: 'Nom de l\'agent',
      description: 'Donnez un nom à votre agent, par exemple "Assistant Support".',
      position: 'right',
      step: 5,
      totalSteps: 9,
    },
    agent_subtitle: {
      targetSelector: '[data-onboarding="agent-subtitle-input"]',
      title: 'Sous-titre',
      description: 'Ajoutez une description courte pour identifier facilement votre agent.',
      position: 'right',
      step: 6,
      totalSteps: 9,
    },
    agent_instructions: {
      targetSelector: '[data-onboarding="agent-instructions-input"]',
      title: 'Instructions personnalisées',
      description: 'Définissez le comportement de votre agent : ton, style, règles à suivre...',
      position: 'right',
      step: 7,
      totalSteps: 9,
    },
    agent_team: {
      targetSelector: '[data-onboarding="agent-team-select"]',
      title: 'Assigner à l\'équipe',
      description: 'Sélectionnez l\'équipe que vous venez de créer pour y rattacher cet agent.',
      position: 'left',
      step: 8,
      totalSteps: 9,
    },
    agent_create: {
      targetSelector: '[data-onboarding="agent-create-btn"]',
      title: 'Créer l\'agent',
      description: 'Cliquez sur "Créer" pour finaliser la création de votre agent.',
      position: 'left',
      step: 9,
      totalSteps: 9,
    },
    // Test flow (separate steps)
    click_edit: {
      targetSelector: '[data-onboarding="agent-edit-btn"]',
      title: 'Configurer votre agent',
      description: 'Cliquez sur "Modifier" pour ouvrir les paramètres de votre agent et lui assigner votre base de connaissances.',
      position: 'top',
      step: 1,
      totalSteps: 6,
    },
    open_knowledge_tab: {
      targetSelector: '[data-onboarding="knowledge-tab"]',
      title: 'Ouvrir Base de connaissances',
      description: 'Cliquez sur l\'onglet "Base de connaissances" pour assigner une collection à votre agent.',
      position: 'bottom',
      step: 2,
      totalSteps: 6,
    },
    assign_knowledge: {
      targetSelector: '[data-onboarding="knowledge-base-section"]',
      title: 'Assigner la collection',
      description: 'Cochez la collection que vous venez de créer pour que votre agent puisse l\'utiliser dans ses réponses.',
      position: 'right',
      step: 3,
      totalSteps: 6,
    },
    save_knowledge: {
      targetSelector: '[data-onboarding="agent-save-btn"]',
      title: 'Enregistrer',
      description: 'Cliquez sur Enregistrer pour sauvegarder la configuration de votre agent.',
      position: 'right',
      step: 4,
      totalSteps: 6,
    },
    test_agent: {
      targetSelector: '[data-onboarding="sandbox-chat-input"]',
      title: 'Interrogez votre agent',
      description: 'Posez une question à votre agent sur le contenu que vous avez ajouté.',
      position: 'top',
      step: 5,
      totalSteps: 6,
    },
    waiting_response: {
      targetSelector: '[data-onboarding="sandbox-chat"]',
      title: 'Réponse en cours…',
      description: 'Veuillez patienter pendant que votre agent génère sa réponse.',
      position: 'left',
      step: 6,
      totalSteps: 6,
    },
    complete: {
      targetSelector: 'body',
      title: '',
      description: '',
      position: 'bottom',
      step: 0,
      totalSteps: 0,
    },
  };

  const config = tooltipConfigs[currentStep];
  if (!config) return null;

  return (
    <OnboardingTooltip
      targetSelector={config.targetSelector}
      title={config.title}
      description={config.description}
      step={config.step}
      totalSteps={config.totalSteps}
      position={config.position}
      onSkip={handleSkip}
      showNextButton={false}
      isVisible={true}
    />
  );
}

export default AgentOnboardingGuide;
